import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../../data";
import { useAccount } from "../../app/session";
import { PillView, useAction } from "../../components/ui";
import { endGrant, openGrant, openSession, type SupportData } from "./data";
import { useSetup, useSupport } from "./hooks";
import {
  DURATIONS, MAX_DURATION_LABEL, grantPill, grantState, orgStatusPill, protocolEntries, setupSteps, splitReason, utcDateTime, validateGrant,
  type GrantErrors, type GrantRow,
} from "./model";
import { DemoHinweis, Fehler, Laden } from "./parts";
import "./betrieb.css";

/**
 * Support-Zugang (17_betreiber/support.html). Die Datenbank kennt den befristeten, begründeten, nur lesenden Zugang
 * (support_access_grants, höchstens 72 Stunden), den Beginn einer Sitzung (open_support_session) und das Beenden durch
 * Support oder Admin. Eine Freigabe durch den Admin vor dem Start, eigene Felder für Ticket und Umfang
 * sowie ändernden Zugriff gibt es nicht: Das Ticket steht vorn in der Begründung, der Umfang ist immer „nur Lesen“.
 */
export function Support() {
  const q = useSupport();
  const [params] = useSearchParams();
  if (q.error) return <><title>Support-Zugang · CreatorOffice</title><h1>Support-Zugang</h1><Fehler error={q.error} retry={() => void q.refetch()} /></>;
  if (q.isPending || !q.data) return <><title>Support-Zugang · CreatorOffice</title><h1>Support-Zugang</h1><Laden /></>;
  return <SupportSeite data={q.data} preselect={params.get("agentur")} />;
}

function SupportSeite({ data, preselect }: { data: SupportData; preselect: string | null }) {
  const nowMs = Date.now();
  const names = useMemo(() => new Map(data.rows.map((r) => [r.org_id, r.name])), [data.rows]);
  const nameOf = (orgId: string) => names.get(orgId) ?? "Unbekannte Agentur";
  const grants = data.grants.map((g) => ({ g, state: grantState(g, nowMs) }));
  const active = grants.filter((x) => x.state === "active").map((x) => x.g);
  const protocol = protocolEntries(data.grants, data.log, nowMs);
  const [sessionOrg, setSessionOrg] = useState<string | null>(null);
  const { busy, error, run } = useAction();

  async function startSession(g: GrantRow) {
    const ok = await run(`sitzung-${g.id}`, () => openSession(api, g.org_id), "Sitzung begonnen und im Protokoll der Agentur vermerkt.");
    if (ok) setSessionOrg(g.org_id);
  }
  async function end(g: GrantRow) {
    const ok = await run(`ende-${g.id}`, async () => {
      if (!(await endGrant(api, g.id))) throw new Error("Der Zugang war schon beendet.");
      return true;
    }, "Support-Zugang beendet.");
    if (ok && sessionOrg === g.org_id) setSessionOrg(null);
  }
  const sessionGrant = sessionOrg ? active.find((g) => g.org_id === sessionOrg) ?? null : null;

  return (
    <>
      <title>Support-Zugang · CreatorOffice</title>
      <h1>Support-Zugang</h1>

      {active.map((g) => {
        const { ticket } = splitReason(g.reason);
        return (
          <div key={g.id} className="notice tk-support-active" role="status">
            <p>
              <strong>Support-Zugang aktiv.</strong> Zugriff bis {utcDateTime(g.expires_at)}, Umfang: nur Lesen, Ticket: {ticket ?? "ohne"}.
              Agentur: <Link to={`/betrieb/agenturen/${g.org_id}`}>{nameOf(g.org_id)}</Link>.
            </p>
            <div className="row">
              {sessionOrg === g.org_id ? (
                <span className="pill success"><span className="pill-zeichen">✓</span> Sitzung läuft</span>
              ) : (
                <button className="" disabled={!!busy} aria-busy={busy === `sitzung-${g.id}`} onClick={() => void startSession(g)}>Sitzung beginnen</button>
              )}
              <button className="danger" disabled={!!busy} aria-busy={busy === `ende-${g.id}`} onClick={() => void end(g)}>Zugang beenden</button>
            </div>
          </div>
        );
      })}
      {error && <p className="tk-error" role="alert">{error}</p>}

      <div className="grid">
        <ZugangForm key={preselect ?? "neu"} data={data} preselect={preselect} />
        <section className="panel stack" aria-labelledby="protokoll-titel">
          <h2 id="protokoll-titel">Zugriffsprotokoll</h2>
          {protocol.length ? (
            <ol>
              {protocol.map((p) => <li key={p.key}>{utcDateTime(p.at)} · {nameOf(p.orgId)} · {p.text}</li>)}
            </ol>
          ) : (
            <p>Noch kein Support-Zugang von diesem Konto.</p>
          )}
          <p className="source-note">
            Das Protokoll zeigt die Zugänge dieses Kontos. Beginn und Ende jeder Sitzung stehen zusätzlich im Protokoll der Agentur,
            das der Admin liest. Einzelne Lesezugriffe protokolliert die Datenbank nicht.
          </p>
        </section>
      </div>

      {sessionGrant && <Diagnose orgId={sessionGrant.org_id} name={nameOf(sessionGrant.org_id)} />}

      <section className="panel stack" aria-labelledby="zugaenge-titel">
        <h2 id="zugaenge-titel">Zugänge dieses Kontos</h2>
        <div className="tablewrap">
          <table>
            <caption className="sr-only">Support-Zugänge dieses Kontos</caption>
            <thead>
              <tr>
                <th scope="col">Agentur</th>
                <th scope="col">Ticket und Begründung</th>
                <th scope="col">Beginn</th>
                <th scope="col">Ende</th>
                <th scope="col">Status</th>
                <th scope="col"><span className="sr-only">Aktionen</span></th>
              </tr>
            </thead>
            <tbody>
              {grants.map(({ g, state }) => {
                const r = splitReason(g.reason);
                return (
                  <tr key={g.id}>
                    <td><Link to={`/betrieb/agenturen/${g.org_id}`}>{nameOf(g.org_id)}</Link></td>
                    <td>{r.ticket ?? "Ohne Ticket"}<small>{r.text}</small></td>
                    <td>{utcDateTime(g.starts_at)}</td>
                    <td>{utcDateTime(g.revoked_at ?? g.expires_at)}{!g.revoked_at && state !== "expired" && <small>geplant</small>}</td>
                    <td><PillView pill={grantPill(state)} /></td>
                    <td>
                      {state === "active" && (
                        <button className="danger" disabled={!!busy} onClick={() => void end(g)} aria-label={`Zugang für ${nameOf(g.org_id)} beenden`}>Zugang beenden</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {grants.length === 0 && <tr><td colSpan={6}>Noch kein Support-Zugang von diesem Konto.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
      <DemoHinweis />
    </>
  );
}

function ZugangForm({ data, preselect }: { data: SupportData; preselect: string | null }) {
  const { account } = useAccount();
  const orgIds = data.rows.map((r) => r.org_id);
  const [orgId, setOrgId] = useState(preselect && orgIds.includes(preselect) ? preselect : orgIds[0] ?? "");
  const [ticket, setTicket] = useState("");
  const [minutes, setMinutes] = useState(DURATIONS[0]!.minutes);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<GrantErrors>({});
  const { busy, error, run } = useAction();

  async function submit(e: FormEvent) {
    e.preventDefault();
    const input = { orgId, ticket, reason, minutes };
    const errs = validateGrant(input, orgIds);
    setErrors(errs);
    if (Object.keys(errs).length || !account) return;
    const created = await run("oeffnen", () => openGrant(api, account.session.userId, input, orgIds), "Support-Zugang geöffnet.");
    if (created) {
      setTicket("");
      setReason("");
    }
  }

  return (
    <form className="panel stack" onSubmit={(e) => void submit(e)} noValidate>
      <h2>Zeitlich begrenzten Zugriff öffnen</h2>
      <label>
        Agentur
        <select id="agentur" value={orgId} onChange={(e) => setOrgId(e.target.value)} aria-invalid={errors.orgId ? true : undefined} aria-describedby={errors.orgId ? "agentur-fehler" : undefined}>
          {data.rows.map((r) => <option key={r.org_id} value={r.org_id}>{r.name} · {orgStatusPill(r.status).label}</option>)}
        </select>
        {errors.orgId && <small id="agentur-fehler" className="tk-error">{errors.orgId}</small>}
      </label>
      <label>
        Ticket
        <input id="ticket" type="text" value={ticket} placeholder="SUP-2026-012" maxLength={40} onChange={(e) => setTicket(e.target.value)}
          aria-invalid={errors.ticket ? true : undefined} aria-describedby={errors.ticket ? "ticket-fehler" : "ticket-hinweis"} />
        {errors.ticket
          ? <small id="ticket-fehler" className="tk-error">{errors.ticket}</small>
          : <small id="ticket-hinweis">Optional. Steht vorn in der Begründung, ein eigenes Feld gibt es nicht.</small>}
      </label>
      <label>
        Dauer
        <select id="dauer" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))}>
          {DURATIONS.map((d) => <option key={d.minutes} value={d.minutes}>{d.label}</option>)}
        </select>
      </label>
      <label>
        Umfang
        <select id="umfang" defaultValue="read" aria-describedby="umfang-hinweis">
          <option value="read">Nur Lesen · technische Diagnose</option>
          <option value="write" disabled>Begrenzte Änderung · nach Freigabe</option>
        </select>
        <small id="umfang-hinweis">Ändernden Zugriff des Supports sieht die Datenbank nicht vor.</small>
      </label>
      <label>
        Begründung
        <textarea id="begruendung" required minLength={10} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="Darstellungsproblem in der Monatsübersicht nachvollziehen."
          aria-invalid={errors.reason ? true : undefined} aria-describedby={errors.reason ? "begruendung-fehler" : undefined} />
        {errors.reason && <small id="begruendung-fehler" className="tk-error">{errors.reason}</small>}
      </label>
      <p>
        Der Zugang gilt sofort, nur lesend und höchstens {MAX_DURATION_LABEL}. Eine Freigabe durch den Admin vor dem Start sieht die Datenbank
        noch nicht vor: Er sieht den Zugang in seinen Team-Einstellungen und kann ihn jederzeit beenden. Kein stilles Anmelden als Agentur.
      </p>
      {error && <p className="tk-error" role="alert">{error}</p>}
      <button className="primary" type="submit" disabled={!!busy || !account || orgIds.length === 0} aria-busy={busy === "oeffnen"}>
        {busy === "oeffnen" ? "Wird geöffnet …" : "Zugang öffnen"}
      </button>
    </form>
  );
}

function Diagnose({ orgId, name }: { orgId: string; name: string }) {
  const q = useSetup(orgId);
  const steps = q.data ? setupSteps(q.data) : [];
  return (
    <section className="panel stack" aria-labelledby="diagnose-titel" aria-live="polite">
      <div className="row between">
        <h2 id="diagnose-titel">Technische Diagnose · {name}</h2>
        <button className="" onClick={() => void q.refetch()} disabled={q.isFetching}>Neu laden</button>
      </div>
      <p>Nur lesend; der Beginn der Sitzung steht im Protokoll der Agentur. Gezeigt wird, welche Angaben der Einrichtung fehlen, nie ihre Werte.</p>
      {q.isPending ? <Laden /> : q.error ? <Fehler error={q.error} retry={() => void q.refetch()} /> : !q.data ? (
        <p>Kein Stand der Einrichtung lesbar.</p>
      ) : q.data.complete ? (
        <p><span className="pill success"><span className="pill-zeichen">✓</span> Einrichtung vollständig</span></p>
      ) : (
        <ul>
          {steps.map((s) => <li key={s.step}>{s.label}: {s.count === 1 ? "1 Angabe fehlt" : `${s.count} Angaben fehlen`}</li>)}
        </ul>
      )}
    </section>
  );
}
